"use client";

import { useSearchParams } from "next/navigation";
import { useEffect } from "react";

type PpcParams = Partial<
  Record<
    | "utm_source"
    | "utm_medium"
    | "utm_campaign"
    | "utm_term"
    | "utm_content"
    | "gclid"
    | "gbraid"
    | "wbraid"
    | "fbclid"
    | "msclkid",
    string
  >
>;

function pickPpcParams(params: URLSearchParams): PpcParams {
  const keys: Array<keyof PpcParams> = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
    "gbraid",
    "wbraid",
    "fbclid",
    "msclkid",
  ];

  const out: PpcParams = {};
  for (const key of keys) {
    const value = params.get(key);
    if (typeof value === "string" && value.trim().length > 0) out[key] = value;
  }
  return out;
}

function safeJsonParse(value: string | null): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

/**
 * Client-only PPC param capture (safe for SSG pages).
 *
 * Usage (in a Server Component page):
 *   <Suspense fallback={null}><PpcTracker /></Suspense>
 *
 * Why: reading `searchParams` in a Server Component can make a route dynamic and
 * break CDN caching. This keeps the HTML 100% static and captures params after hydration.
 */
export function PpcTracker({
  storageKey = "ubp:ppc",
}: {
  storageKey?: string;
}) {
  const searchParams = useSearchParams();
  const queryString = searchParams.toString();

  useEffect(() => {
    const rawQuery =
      queryString.length > 0
        ? queryString
        : typeof window !== "undefined"
          ? window.location.search.replace(/^\?/, "")
          : "";

    const ppc = pickPpcParams(new URLSearchParams(rawQuery));
    const keys = Object.keys(ppc);
    if (keys.length === 0) return;

    const nowIso = new Date().toISOString();

    try {
      const existing = safeJsonParse(window.sessionStorage.getItem(storageKey));
      const existingRecord =
        typeof existing === "object" && existing !== null
          ? (existing as Record<string, unknown>)
          : {};

      const merged = {
        ...existingRecord,
        ...ppc,
        firstSeenAt: existingRecord.firstSeenAt ?? nowIso,
        lastSeenAt: nowIso,
      };

      window.sessionStorage.setItem(storageKey, JSON.stringify(merged));
      window.dispatchEvent(new Event("ubp:ppc"));
    } catch {
      // Intentionally ignore storage failures (private mode, blocked storage, etc.)
    }

    // Optional: push to GTM/analytics without requiring any specific vendor.
    try {
      const dataLayer = (window as unknown as { dataLayer?: unknown[] }).dataLayer;
      if (Array.isArray(dataLayer)) {
        dataLayer.push({ event: "ppc_params", ...ppc });
      }
    } catch {
      // Ignore analytics failures.
    }
  }, [queryString, storageKey]);

  return null;
}

